import type { ReactNode } from 'react'

/**
 * The small part of Markdown a help topic actually uses.
 *
 * A Markdown library would be a third of this bundle for a body that the
 * documentation build has already validated: headings, paragraphs, lists,
 * inline code, bold, and cross-application links written as `[text](sf:id)`.
 * Anything richer than that is a topic the manual renders and the drawer does
 * not, and the drawer says so rather than guessing.
 *
 * Nothing here interprets HTML. Topic bodies are repository content, not user
 * input, but a help drawer that evaluates HTML is a help drawer that evaluates
 * whatever ends up in a topic later — and `dangerouslySetInnerHTML` in a
 * codebase is a door somebody walks through eventually.
 */

type Inline = { text: string; code?: boolean; strong?: boolean; href?: string }

const INLINE = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)]+\))/g

function splitInline(source: string): Inline[] {
  const parts: Inline[] = []
  let index = 0
  for (const match of source.matchAll(INLINE)) {
    const at = match.index ?? 0
    if (at > index) parts.push({ text: source.slice(index, at) })
    const token = match[0]
    if (token.startsWith('`')) {
      parts.push({ text: token.slice(1, -1), code: true })
    } else if (token.startsWith('**')) {
      parts.push({ text: token.slice(2, -2), strong: true })
    } else {
      const close = token.indexOf(']')
      parts.push({ text: token.slice(1, close), href: token.slice(close + 2, -1) })
    }
    index = at + token.length
  }
  if (index < source.length) parts.push({ text: source.slice(index) })
  return parts
}

function renderInline(source: string, keyPrefix: string): ReactNode[] {
  return splitInline(source).map((part, at) => {
    const key = `${keyPrefix}-${at}`
    if (part.code) return <code key={key}>{part.text}</code>
    if (part.strong) return <strong key={key}>{part.text}</strong>
    if (part.href !== undefined) {
      // `sf:` links point at another topic, and a sibling application that is
      // not installed must render as plain text — never a dead link and never
      // a 404. Resolving those is the platform's job; until the drawer asks
      // it, the honest rendering is the text.
      if (part.href.startsWith('sf:')) return <span key={key}>{part.text}</span>
      return (
        <a key={key} href={part.href} rel="noreferrer noopener" target="_blank">
          {part.text}
        </a>
      )
    }
    return <span key={key}>{part.text}</span>
  })
}

export function Markdown({ body }: { body: string }) {
  const blocks: ReactNode[] = []
  const lines = body.split('\n')
  let paragraph: string[] = []
  let list: string[] = []
  let ordered = false

  const flushParagraph = () => {
    if (paragraph.length === 0) return
    const key = `p${blocks.length}`
    blocks.push(<p key={key}>{renderInline(paragraph.join(' '), key)}</p>)
    paragraph = []
  }
  const flushList = () => {
    if (list.length === 0) return
    const key = `l${blocks.length}`
    const items = list.map((item, at) => <li key={`${key}-${at}`}>{renderInline(item, `${key}-${at}`)}</li>)
    blocks.push(ordered ? <ol key={key}>{items}</ol> : <ul key={key}>{items}</ul>)
    list = []
  }

  for (const raw of lines) {
    const line = raw.trimEnd()
    if (line.trim() === '') {
      flushParagraph()
      flushList()
      continue
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(line)
    if (heading) {
      flushParagraph()
      flushList()
      const key = `h${blocks.length}`
      blocks.push(
        <h4 key={key} className="help-heading">
          {renderInline(heading[2] ?? '', key)}
        </h4>,
      )
      continue
    }
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line)
    if (bullet) {
      flushParagraph()
      if (ordered) flushList()
      ordered = false
      list.push(bullet[1] ?? '')
      continue
    }
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line)
    if (numbered) {
      flushParagraph()
      if (!ordered) flushList()
      ordered = true
      list.push(numbered[1] ?? '')
      continue
    }
    // A line that is not a marker, while a list is open, is the rest of the
    // item above it. Topic bodies are hard-wrapped in the repository, so
    // treating every wrapped line as a new paragraph broke each item in half
    // and restarted the numbering at one on the next.
    if (list.length > 0 && paragraph.length === 0) {
      list[list.length - 1] = `${list[list.length - 1] ?? ''} ${line.trim()}`
      continue
    }
    flushList()
    paragraph.push(line.trim())
  }
  flushParagraph()
  flushList()

  return <div className="help-body">{blocks}</div>
}
